import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TransformationRunStats } from '../ActivationSyncHistory/models/sync-history-item';
import { HdButton, HdTooltip } from '../../../components';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import {
  TRACKER_ACTIVATION_SYNC_HISTORY_DUPLICATE_RECORDS_SHOW_SAMPLE_CLICK,
  TRACKER_ACTIVATION_SYNC_HISTORY_RECORDS_IGNORED_FETCH_FAILED,
  TRACKER_ACTIVATION_SYNC_HISTORY_RECORDS_IGNORED_FETCH_SUCCESS
} from '../tracking';
import ActivationsAPI from '../api/ActivationsAPI';
import { PayloadViewerDialog } from '../../../modal/components/payload-viewer-dialog/PayloadViewerDialog';
import HdModal from '../../../modal/components/modal/HdModal';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';

interface RecordsIgnoredProps {
  activationRunId: number;
  transformationRunStats: TransformationRunStats;
}

const dataIdGenerator = getDataIdGenerator('activation-history-ignored-records');

function RecordsIgnored({ activationRunId, transformationRunStats }: RecordsIgnoredProps) {
  const [currentDuplicateRecordInd, setCurrentDuplicateRecordInd] = useState(0);
  const [duplicateRecordSamples, setDuplicateRecordSamples] = useState([]);
  const [sampleDialogOpen, setSampleDialogOpen] = useState(false);
  const analyticsTracker = useAnalyticsTracker();
  const editorRef = useRef(null);

  useEffect(() => {
    getDuplicateRecords();
  }, []);

  const getDuplicateRecords = () => {
    return ActivationsAPI.getActivationRunDuplicates(activationRunId)
      .then(res => {
        if (!res.data?.duplicates_list) {
          return [];
        }

        analyticsTracker.eventTrack({
          action: TRACKER_ACTIVATION_SYNC_HISTORY_RECORDS_IGNORED_FETCH_SUCCESS,
          properties: {
            activationRunId: activationRunId,
            failureTypeCount: res.data.duplicates_list.length
          }
        });

        return res.data.duplicates_list;
      })
      .then(duplicates => {
        setDuplicateRecordSamples(duplicates.map(duplicate => duplicate.sample_record));
      })
      .catch(err => {
        analyticsTracker.eventTrack({
          action: TRACKER_ACTIVATION_SYNC_HISTORY_RECORDS_IGNORED_FETCH_FAILED,
          properties: {
            activationRunId: activationRunId,
            errorMessage: err.message
          }
        });
      });
  };

  const isDuplicateRecordsPresent = useMemo(() => {
    return transformationRunStats.recordsIgnored.duplicateRecords > 0;
  }, [transformationRunStats]);

  const showSampleDialog = () => {
    analyticsTracker.eventTrack({
      action: TRACKER_ACTIVATION_SYNC_HISTORY_DUPLICATE_RECORDS_SHOW_SAMPLE_CLICK,
      properties: {
        activationRunId: activationRunId
      }
    });

    setSampleDialogOpen(true);
  };

  const closePayloadDialog = () => {
    setSampleDialogOpen(false);
    setCurrentDuplicateRecordInd(0);
  };

  const getPayloadPromise = (order: 'prev' | 'next') => {
    const duplicateRecordsCount = duplicateRecordSamples.length;
    let duplicateRecordsInd = currentDuplicateRecordInd;

    if (order === 'prev' && currentDuplicateRecordInd > 0) {
      setCurrentDuplicateRecordInd(currentDuplicateRecordInd - 1);
    }

    if (order === 'next') {
      duplicateRecordsInd = (currentDuplicateRecordInd + 1) % duplicateRecordsCount;
      setCurrentDuplicateRecordInd(duplicateRecordsInd);
    }

    return Promise.resolve({
      payload: duplicateRecordSamples[duplicateRecordsInd],
      hasNext: duplicateRecordsInd < duplicateRecordsCount - 1,
      hasPrev: duplicateRecordsInd > 0
    });
  };

  return (
    <div className='card-table card-table-header px-4 py-0' data-id={dataIdGenerator('')}>
      <div className='center-flex-row'>
        <div
          className={`center-flex-row py-3 mr-8 ${isDuplicateRecordsPresent ? 'mr-10' : 'mr-8'}`}
        >
          Duplicate Records

          <span className='badge badge-solid badge-solid-xs ml-1'>
            {transformationRunStats.recordsIgnored.duplicateRecords || 0}
          </span>

          {isDuplicateRecordsPresent && (
            <span>
              <span className='text-secondary mx-3'>&bull;</span>

              <HdTooltip title='View Duplicate Records'>
                <HdButton
                  hdButton
                  size='sm'
                  variation='faded'
                  color='primary'
                  icon='sample-event'
                  type='button'
                  onClick={() => showSampleDialog()}
                  dataId={dataIdGenerator('duplicate-records-sample')}
                >
                  Sample Records
                </HdButton>
              </HdTooltip>
            </span>
          )}
        </div>

        <div className='center-flex-row py-3 mr-8'>
          Permanent Failures

          <span className='badge badge-solid badge-solid-xs ml-1'>
            {transformationRunStats.recordsIgnored.permanentFailures || 0}
          </span>
        </div>

        <div className='center-flex-row py-3 mr-8'>
          Already Synced Records

          <span className='badge badge-solid badge-solid-xs ml-1'>
            {transformationRunStats.recordsIgnored.alreadySyncedRecords || 0}
          </span>
        </div>
      </div>

      <HdModal
        dataId={dataIdGenerator('modal')}
        open={sampleDialogOpen}
        onClose={closePayloadDialog}
        placement='top'
        styling={{ width: '760px' }}
        onTransitionEnd={() => editorRef.current.refresh()}
      >
        <PayloadViewerDialog
          title='Duplicate Records Ignored'
          renderPrev={true}
          renderNext={true}
          onNegativeClick={closePayloadDialog}
          getPayloadPromise={getPayloadPromise}
          editorRef={editorRef}
        />
      </HdModal>
    </div>
  );
}

export default RecordsIgnored;
